import React, { useEffect, useRef } from 'react';
import _ from 'lodash';
import logger from '../../../utils/logger';

/**
 * Unified polling component for table updates.
 * This isolates polling logic to prevent unnecessary re-renders of table components.
 * Only triggers callbacks when actual changes are detected, preventing expensive re-renders
 * on every polling tick when there are no new results.
 * 
 * @param {Object} config - Configuration object
 * @param {Function} config.getData - Optional function that returns the current data to poll (for data comparison mode)
 * @param {Function} config.shouldPoll - Function that returns true if polling should continue
 * @param {Function} config.isComplete - Optional function that returns true if data is complete (stops polling)
 * @param {Function} config.onUpdate - Callback function called when data changes or on each poll (if no data comparison)
 * @param {Function} config.onComplete - Optional callback when data is complete
 * @param {Function} config.getAdditionalState - Optional function to get additional state for comparison
 * @param {Function} config.onPollTick - Optional callback called on every poll tick (for non-data-comparison mode)
 * @param {number} config.interval - Polling interval in milliseconds (default: 1000)
 * @param {string} config.name - Name for logging purposes
 * @param {boolean} config.enableLogging - Whether to enable console logs (default: false)
 * @param {boolean} config.compareData - Whether to compare data for changes (default: true if getData provided)
 */
const TablePollingComponent = ({
  getData,
  shouldPoll,
  isComplete,
  onUpdate,
  onComplete,
  getAdditionalState,
  onPollTick,
  interval = 1000,
  name = 'TablePolling',
  enableLogging = false,
  compareData = undefined
}) => {
  const intervalRef = useRef(null);
  const callbackRef = useRef(onUpdate);
  const completeCallbackRef = useRef(onComplete);
  const getDataRef = useRef(getData);
  const shouldPollRef = useRef(shouldPoll);
  const isCompleteRef = useRef(isComplete);
  const getAdditionalStateRef = useRef(getAdditionalState);
  const onPollTickRef = useRef(onPollTick);
  const previousDataRef = useRef(null);
  const previousAdditionalStateRef = useRef(null);
  
  // Calculate shouldCompareData - use compareData if explicitly provided, otherwise check if getData exists
  const shouldCompareData = compareData !== undefined ? compareData : !!getData;

  // Update refs when functions change (without triggering effect re-run)
  useEffect(() => {
    callbackRef.current = onUpdate;
    getDataRef.current = getData;
    shouldPollRef.current = shouldPoll;
    isCompleteRef.current = isComplete;
    getAdditionalStateRef.current = getAdditionalState;
    onPollTickRef.current = onPollTick;
    completeCallbackRef.current = onComplete;
  }, [onUpdate, onComplete, getData, shouldPoll, isComplete, getAdditionalState, onPollTick]);

  // Polling logic
  useEffect(() => {
    // Initial check on mount or when dependencies change
    const currentData = getDataRef.current ? getDataRef.current() : null;
    const currentAdditionalState = getAdditionalStateRef.current ? getAdditionalStateRef.current() : null;
    const shouldStartPolling = shouldPollRef.current();

    if (enableLogging) {
      logger.log(`🔄 ${name}: Initial check`, {
        hasData: !!currentData,
        dataLength: Array.isArray(currentData) ? currentData.length : 'N/A',
        shouldPoll: shouldStartPolling,
        compareData: shouldCompareData
      });
    }

    // If comparing data, do initial update if data exists or if we should poll
    if (shouldCompareData && (currentData || shouldStartPolling)) {
      const dataChanged = !_.isEqual(previousDataRef.current, currentData);
      const additionalStateChanged = getAdditionalStateRef.current
        ? !_.isEqual(previousAdditionalStateRef.current, currentAdditionalState)
        : false;

      if (dataChanged || additionalStateChanged) {
        previousDataRef.current = currentData ? _.cloneDeep(currentData) : null;
        previousAdditionalStateRef.current = currentAdditionalState 
          ? _.cloneDeep(currentAdditionalState) 
          : null;

        if (enableLogging) {
          logger.log(`📦 ${name}: Initial data update triggered`, {
            dataLength: Array.isArray(currentData) ? currentData.length : 'N/A'
          });
        }

        if (callbackRef.current) {
          callbackRef.current(currentData, currentAdditionalState);
        }
      }
    }

    // Check if data is already complete
    const isDataComplete = isCompleteRef.current ? isCompleteRef.current(currentData) : false;

    // Only set up polling if we should poll and data is not complete
    const shouldStartInterval = shouldStartPolling && !isDataComplete;

    if (!shouldStartInterval) {
      if (enableLogging) {
        if (isDataComplete) {
          logger.log(`✅ ${name}: Data complete, not polling`);
        } else {
          logger.log(`⏸️ ${name}: Not polling - conditions not met`);
        }
      }
      return;
    }

    if (enableLogging) {
      logger.log(`▶️ ${name}: Starting polling interval (${interval}ms)`);
    }

    let intervalId;

    // Poll at specified interval
    intervalId = setInterval(() => {
      const storeData = getDataRef.current ? getDataRef.current() : null;
      const storeAdditionalState = getAdditionalStateRef.current ? getAdditionalStateRef.current() : null;

      // Check if data is complete - stop polling if so
      const isCompleteNow = isCompleteRef.current ? isCompleteRef.current(storeData) : false;

      if (isCompleteNow) {
        if (enableLogging) {
          logger.log(`✅ ${name}: Data complete, stopping polling`);
        }

        // Clear the interval
        if (intervalId) {
          clearInterval(intervalId);
        }

        // Final update to ensure UI is in sync
        if (completeCallbackRef.current) {
          completeCallbackRef.current(storeData, storeAdditionalState);
        } else if (callbackRef.current) {
          callbackRef.current(storeData, storeAdditionalState);
        }

        return;
      }

      // Check if we should still be polling
      const shouldContinuePolling = shouldPollRef.current();
      if (!shouldContinuePolling) {
        if (enableLogging) {
          logger.log(`⏸️ ${name}: Polling conditions no longer met, stopping`);
        }
        if (intervalId) {
          clearInterval(intervalId);
        }
        return;
      }

      if (shouldCompareData) {
        // Data comparison mode - only update if data changed
        const dataChanged = !_.isEqual(previousDataRef.current, storeData);
        const additionalStateChanged = getAdditionalStateRef.current
          ? !_.isEqual(previousAdditionalStateRef.current, storeAdditionalState)
          : false;

        if (enableLogging) {
          logger.log(`⏱️ ${name}: Polling tick`, {
            dataLength: Array.isArray(storeData) ? storeData.length : 'N/A',
            dataChanged,
            additionalStateChanged
          });
        }

        // Only trigger update if data actually changed
        if (dataChanged || additionalStateChanged) {
          if (enableLogging) {
            logger.log(`✅ ${name}: Data changed, updating table`, {
              previousLength: Array.isArray(previousDataRef.current) 
                ? previousDataRef.current.length 
                : 'N/A',
              newLength: Array.isArray(storeData) ? storeData.length : 'N/A'
            });
          }

          previousDataRef.current = storeData ? _.cloneDeep(storeData) : null;
          previousAdditionalStateRef.current = storeAdditionalState
            ? _.cloneDeep(storeAdditionalState)
            : null;

          if (callbackRef.current) {
            callbackRef.current(storeData, storeAdditionalState);
          }
        } else {
          if (enableLogging) {
            logger.log(`⏭️ ${name}: No changes detected, skipping update`);
          }
        }
      } else {
        // Non-data-comparison mode - call callback on every tick
        if (enableLogging) {
          logger.log(`⏱️ ${name}: Polling tick (no data comparison)`);
        }

        // Call onPollTick if provided, otherwise call onUpdate
        if (onPollTickRef.current) {
          onPollTickRef.current(storeData, storeAdditionalState);
        } else if (callbackRef.current) {
          callbackRef.current(storeData, storeAdditionalState);
        }
      }
    }, interval);

    intervalRef.current = intervalId;

    return () => {
      if (intervalId) {
        if (enableLogging) {
          logger.log(`🛑 ${name}: Clearing polling interval`);
        }
        clearInterval(intervalId);
      }
    };
  }, [interval, name, enableLogging, shouldCompareData]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  // This component doesn't render anything - it's purely for side effects
  return null;
};

export default React.memo(TablePollingComponent);

